import { Box, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import classNames from 'classnames';
import React, { FC, Fragment, useCallback, useMemo } from 'react';
import { TeamVsTeamEntity } from 'shared/interfaces';
import { ValueOrDefaultFormat } from '../Format/ValueOrDefaultFormat';
import { TeamIcon } from '../TeamIcon';
import { useStyles } from './TeamVsTeam.styles';

interface TeamVsTeamProps {
  teams: TeamVsTeamEntity[];
  score?: number[];
  divider?: string;
}

export const TeamVsTeam: FC<TeamVsTeamProps> = ({ teams, score, divider }) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const isLessThanXxs = useMediaQuery(theme.breakpoints.down('x-xs'));

  const normalizedTeams = useMemo(() => {
    if (!teams.length) {
      return [null, null];
    }

    return teams.length < 2 ? [...teams, null] : teams;
  }, [teams]);

  const getBorderColor = useCallback(
    (index: number) => (index ? theme.palette.warning.light : theme.palette.primary.light),
    [theme]
  );

  const scoreLabel = useMemo(() => {
    if (!score?.length) {
      return null;
    }

    return (
      <Box className={classes.scoreContainer}>
        <Typography className={classes.score}>{score[0]}</Typography>
        <Typography className={classes.scoreDivider}>:</Typography>
        <Typography className={classes.score}>{score[1]}</Typography>
      </Box>
    );
  }, [score, classes]);

  const dividerContent = useMemo(() => {
    return divider ? <Typography className={classes.divider}>{divider}</Typography> : scoreLabel;
  }, [classes.divider, divider, scoreLabel]);

  return (
    <Box className={classes.root}>
      <Box display="flex" flexDirection="row">
        {normalizedTeams.map((team, index) => (
          <Fragment key={index}>
            <Tooltip enterTouchDelay={0} title={team?.name ?? 'No Opponent'}>
              <Box className={classNames(classes.teamContainer, { revert: index })}>
                <Box className={classNames(classes.textDetailsContainer, { revert: index })}>
                  {team?.status && <Typography className={classes[team.status]} />}
                  <Typography className={classNames(classes.name, { revert: index })}>
                    <ValueOrDefaultFormat defaultValue="No Opponent" value={team?.name} />
                  </Typography>
                </Box>
                {!!team && (
                  <TeamIcon
                    team={team}
                    size={isLessThanXxs ? 34 : 48}
                    borderColor={getBorderColor(index)}
                  />
                )}
              </Box>
            </Tooltip>
            {!index && !isLessThanXxs && <>{dividerContent}</>}
          </Fragment>
        ))}
      </Box>

      {isLessThanXxs && <>{scoreLabel}</>}
    </Box>
  );
};
